'use client';
import { ReactNode, useMemo } from 'react';

export type SvgCircleProgressProps = {
  className?: string;
  style?: React.CSSProperties;
  showText?: boolean;
  //   svg 大小
  size?: number;
  // 百分比，按 0 - 1
  percentage: number;
  // 底色
  innerColor?: string;
  // 外面的颜色
  outerColor?: string | string[];
  // 环外圆的半径
  radius?: number;
  //  圆环的宽度
  ringWidth?: number;
  fontSize?: number;
  textDecimal?: number;
};

export function SvgCircleProgress({
  percentage,
  innerColor = 'lightgray',
  outerColor = 'red',
  showText = true,
  radius = 70,
  ringWidth = 10,
  className,
  style,
  fontSize = 14,
  textDecimal = 2,
}: SvgCircleProgressProps) {
  const pct = Math.min(Math.max(0, percentage), 1);

  const gradientId = useMemo(
    () => `svg-p-c-gradient-${Math.random().toString(16).replace('0.', '')}`,
    []
  );
  const useGradient = Array.isArray(outerColor);
  const size = radius * 2 + ringWidth;
  const centerPoint = [size / 2, size / 2];
  const circleCommonProps = {
    r: radius,
    strokeWidth: ringWidth,
    cx: centerPoint[0],
    cy: centerPoint[1],
  };

  return (
    <svg width={size} height={size} className={className} style={style}>
      {useGradient && (
        <defs>
          <linearGradient id={gradientId} x1="1" y1="0" x2="0" y2="0">
            {outerColor.map((c, index) => {
              const offset = (index * 100) / (outerColor.length - 1);
              return <stop offset={`${offset}%`} stopColor={c} key={c} />;
            })}
          </linearGradient>
        </defs>
      )}
      <g transform={`rotate(-90 ${centerPoint.join(' ')})`}>
        <Circle {...circleCommonProps} color={innerColor} pct={1} />
        <Circle
          {...circleCommonProps}
          color={useGradient ? `url('#${gradientId}')` : outerColor}
          pct={pct}
        ></Circle>
      </g>
      {showText && (
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fill="currentColor"
          fontSize={fontSize}
        >
          {(pct * 100).toFixed(textDecimal)}%
        </text>
      )}
    </svg>
  );
}

const Circle = ({
  color,
  pct = 0.01,
  r,
  cx,
  cy,
  strokeWidth,
  children,
}: {
  color: string;
  pct?: number;
  children?: ReactNode;
  strokeWidth: number;
  r: number;
  cx: number;
  cy: number;
}) => {
  const circ = 2 * Math.PI * r;
  const strokePct = (1 - pct) * circ;
  return (
    <circle
      r={r}
      cx={cx}
      cy={cy}
      fill="transparent"
      stroke={strokePct !== circ ? color : ''} // remove color as 0% sets full circumference
      strokeWidth={strokeWidth}
      strokeDasharray={circ}
      strokeDashoffset={pct ? strokePct : 0}
      strokeLinecap="round"
    >
      {children}
    </circle>
  );
};
